// Browser polyfills — must be imported FIRST in main.jsx, before any module
// that touches Buffer (gray-matter, used by the article loader).
//
// ES modules evaluate in dependency-graph order: a top-level statement in
// this file runs before any subsequent import in main.jsx. That guarantees
// `window.Buffer` is set before articles.js / gray-matter init.

import { Buffer } from 'buffer';

if (typeof window !== 'undefined' && !window.Buffer) {
  window.Buffer = Buffer;
}

// gray-matter also references `process` in some code paths.
if (typeof window !== 'undefined' && !window.process) {
  window.process = { env: {}, browser: true, version: '', platform: 'browser' };
}

// ============================================================
// React reconciliation safety net — defensive removeChild / insertBefore.
//
// Prevents an intermittent HARD CRASH whose stack is
//   removeChildFromContainer → commitDeletionEffectsOnFiber
//   → recursivelyTraverseDeletionEffects
// and which surfaced as the index.html boot fallback
// (« ERREUR D'INITIALISATION — Le site n'a pas pu démarrer. »).
//
// Cause : framer-motion's <AnimatePresence> (used in ~25 components, incl. a
// createPortal into document.body in PWAInstallPrompt) drives EXIT animations
// by mutating the DOM itself. On route changes / fast open-close toggles —
// amplified by React 18 StrictMode in dev, but also seen intermittently in
// prod — framer-motion and React can both try to remove (or re-insert) the
// SAME node. The second native DOM call then hits a node whose parent has
// already changed and throws
//   "NotFoundError: The node to be removed is not a child of this node",
// which React cannot recover from mid-commit → the whole tree unwinds.
//
// Fix (canonical — facebook/react#11538) : make removeChild / insertBefore
// NO-OP gracefully when the parent/child relationship is ALREADY inconsistent
// (the removal/insertion is effectively done already). This never masks a bug
// in correct code — you only ever remove a node that is genuinely a child —
// it only absorbs the redundant double-op from the animation race. Covers dev
// AND prod, every AnimatePresence usage, zero behavioural change to well-formed
// mutations. Must run BEFORE React (this file is imported first in main.jsx).
// ============================================================
if (typeof Node === 'function' && Node.prototype) {
  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function removeChild(child) {
    if (child && child.parentNode !== this) {
      // Already detached / moved (typically by framer-motion) — the removal
      // we were asked to perform is already done. Returning the node matches
      // the native contract without throwing.
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn('[ip] suppressed removeChild on a non-child node (framer-motion/React race)', child);
      }
      return child;
    }
    return originalRemoveChild.apply(this, arguments);
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function insertBefore(newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) {
      // Reference node was moved out from under us — append instead of
      // throwing (insertBefore with a null reference == appendChild).
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn('[ip] insertBefore reference is not a child; appending instead (framer-motion/React race)', referenceNode);
      }
      return originalInsertBefore.call(this, newNode, null);
    }
    return originalInsertBefore.apply(this, arguments);
  };
}
